/**
 * @module app.core
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { LookupService } from 'app/cd/services/lookup-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';
import { bindable } from 'aurelia-typed-observable-plugin';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class EditMemberPlan extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'eligibilityChanged' })
    protected eligibility: CD.MemberEligibility = {};

    @bindable
    protected readonly: boolean = false;
    @bindable
    protected allFields: boolean = true;
    @bindable
    protected planFields: boolean = true;
    @bindable
    protected hideProduct: boolean = true;
    @bindable
    protected healthPlan: CD.HealthPlan | null = null;
    @bindable
    protected lineOfBusiness: CD.LineOfBusiness | null = null;
    @observable protected product: CD.Product | null = null;
    public healthPlanSearchQuery: string | null = null;
    public lineOfBusinessSearchQuery: string | null = null;
    public productSearchQuery: string | null = null;
    protected healthPlans: CD.HealthPlan[] = [];
    protected linesOfBusiness: CD.LineOfBusiness[] = [];
    protected products: CD.Product[] = [];
    @bindable
    protected disableProduct: boolean = true;

    ////****************** Element References ***************************************////
    protected $healthPlansAutoComplete!: EssAutocompleteCustomElement;
    protected $linesOfBusinessAutoComplete!: EssAutocompleteCustomElement;
    protected $productsAutoComplete!: EssAutocompleteCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    ////****************** Functions ************************************************////
    // Update product info on component when member is updated (edited/cleared)
    protected eligibilityChanged(): void {
        const product = this.eligibility.product;
        this.healthPlan = product?.healthPlan || null;
        this.lineOfBusiness = product?.lineOfBusiness || null;
        this.product = product || null;
        this.healthPlans = [];
        this.linesOfBusiness = [];

        this.healthPlanSearchQuery = this.healthPlan?.$display || null;
        this.lineOfBusinessSearchQuery = this.lineOfBusiness?.$display || null;
        this.productSearchQuery = this.product?.$display || null;
        this.toggleProduct();
    }

    protected productChanged(): void {
        if(this.product) {
            ////need this check; otherwise : if you empty out the Product field and cancel the modal, product display disappears
            this.eligibility.product = this.product;
        }
    }

    /** Called when a user clicks on an auto-complete search result */
    protected addHealthPlan(healthPlan: CD.HealthPlan): void {
        this.healthPlan = healthPlan;
        this.resetProduct();
    }

    protected addLineOfBusiness(lineOfBusiness: CD.LineOfBusiness): void {
        this.lineOfBusiness = lineOfBusiness;
        this.resetProduct();
    }

    protected addProduct(product: CD.Product): void {
        this.product = product;
    }

    protected resetProduct() {
        this.productSearchQuery = '';
        this.products = [];
        this.product = null;
        this.$productsAutoComplete.value = null;
        this.toggleProduct();
    }

    protected toggleProduct() {
        if(this.planFields) {
            if(this.healthPlan && this.lineOfBusiness) {
                this.disableProduct = false;
            } else {
                this.disableProduct = true;
            }
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchHealthPlans(searchValue: string): Promise<void> {
        // NOTE: critical to reset autocomplete to re-render if resultant objects are the same as the previous search
        this.healthPlans = [];
        //NOTE: critical to ensure validation works correctly when autocomplete values are emptied out or typos entered
        this.$healthPlansAutoComplete.value = null;
        this.healthPlan = null;
        this.toggleProduct();
        // character limit : min of 2 characters to initiate search // for performance enhancement
        if(searchValue.length < 2) {
            return;
        }

        try {
            this.state.beginLoad();
            const response = await this.lookupService.searchHealthPlans(searchValue, undefined);
            this.healthPlans = response.results;
            if(this.healthPlans.length === 0) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.events.noResults')).show();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async searchLinesOfBusiness(searchValue: string): Promise<void> {
        this.linesOfBusiness = [];
        this.$linesOfBusinessAutoComplete.value = null;
        this.lineOfBusiness = null;
        this.toggleProduct();
        if(searchValue.length < 2) {
            return;
        }

        try {
            this.state.beginLoad();
            const response = await this.lookupService.searchLinesOfBusiness(searchValue, undefined);
            this.linesOfBusiness = response.results;
            if(this.linesOfBusiness.length === 0) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.events.noResults')).show();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async searchProducts(searchValue: string): Promise<void> {
        this.products = [];
        this.$productsAutoComplete.value = null;
        this.product = null;
        if(searchValue.length < 2) {
            return;
        }

        try {
            this.state.beginLoad();
            const response = await this.lookupService.searchProducts(this.healthPlan?.id as string, this.lineOfBusiness?.id as string, searchValue);
            this.products = response.results;
            if(this.products.length === 0) {
                this.toaster.warn().message(this.i18n.tr('ui:messages.events.noResults')).show();
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
